import BigNumber from 'bignumber.js';

import SETTINGS from '#/config/global-settings';

import { Market } from '#/api/markets';
import { MarketSummary } from '#/api/markets-summary';
import { OrderInstruction, OrderType } from '#/api/orders';

import * as MarketFn from '#/features/market/functions';

import nonNullable from '#/utils/nonNullable';

import { OrderFormState } from './form-state';

type FeeType = 'TAKER' | 'MAKER';

/** Maximum fee user is willing to pay */
function getOrderFeePercent(type: FeeType): BigNumber {
  return {
    MAKER: BigNumber(MAKER_FEE),
    TAKER: BigNumber(TAKER_FEE),
  }[type];
}

const ORDER_FEE_PRECISION = 4;

interface InstructionOption {
  readonly value: OrderInstruction;
  readonly label: string;
  readonly selected: boolean;
}

export interface OrderFormView {
  readonly asset: string | null;
  readonly amount: FieldView;
  readonly limitPrice: FieldView;
  readonly type: OrderType;
  readonly instruction: OrderInstruction;
  // Derived:
  readonly isValid: boolean;
  readonly amountBaseAsset: FormattedView;
  readonly notionalQuoteAsset: FormattedView;
  readonly limitFee: FormattedView;
  readonly feeRange: string;
  readonly instructionLabel: string;
  readonly instructionOptions: readonly InstructionOption[];
}

interface FormattedView {
  /** raw value
   * @example '30000.5'
   * @example '0.34'
   */
  readonly value: string;
  /**
   * @example '$30,000.50'
   * @example '0.3400 BTC'
   */
  readonly formattedValue: string;
}

interface FieldView extends FormattedView {
  readonly isValid: boolean;
}

// WIP: needs to be covered with unit tests
export function createView(
  state: OrderFormState,
  activeMarket: Market | null,
  activeMarketSummary: MarketSummary | undefined,
  isSignedIn: boolean,
  isBeingLiquidated: boolean,
): OrderFormView {
  const price =
    state.type === 'MARKET'
      ? activeMarketSummary?.oracle_price ?? null
      : state.limitPrice ?? null;

  const amount = amountView(state, activeMarket);
  const amountBaseAsset = amountBaseAssetView(state, activeMarket, price);
  const limitPrice = limitPriceView(state);
  const limitFee = limitFeeView(state, activeMarket, price);
  const feeRange = feeRangeView(state, activeMarket, price);
  const notionalQuoteAsset = notionalQuoteAssetView(state, activeMarket, price);
  const instruction = instructionView(state);
  const instructionLabel = instructionLabelView(state);
  const instructionOptions = instructionOptionsView(state);

  const isValid =
    !isBeingLiquidated &&
    isSignedIn &&
    [amount, limitPrice].every((field) => field.isValid);

  return {
    asset: state.asset,
    type: state.type,
    instruction,
    instructionLabel,
    instructionOptions,
    amount,
    amountBaseAsset,
    notionalQuoteAsset,
    limitPrice,
    limitFee,
    feeRange,
    isValid,
  };
}

function instructionOptionsView(
  state: OrderFormState,
): OrderFormView['instructionOptions'] {
  const options: ReadonlyArray<InstructionOption | null> = [
    {
      value: 'GTC',
      label: INSTRUCTION_VALUE_LABEL_MAP['GTC'],
      selected: state.instruction === 'GTC',
    },
    {
      value: 'POST_ONLY',
      label: INSTRUCTION_VALUE_LABEL_MAP['POST_ONLY'],
      selected: state.instruction === 'POST_ONLY',
    },
    ENABLE_IOC_ORDER === 'true'
      ? {
          value: 'IOC',
          label: INSTRUCTION_VALUE_LABEL_MAP['IOC'],
          selected: state.instruction === 'IOC',
        }
      : null,
  ];
  return options.filter(nonNullable);
}

function instructionView(state: OrderFormState): OrderFormView['instruction'] {
  switch (state.type) {
    case 'MARKET': {
      return 'GTC';
    }
    case 'LIMIT': {
      return state.instruction;
    }
    // no default
  }
}

const INSTRUCTION_VALUE_LABEL_MAP: Record<OrderInstruction, string> = {
  GTC: 'Good Till Cancelled',
  POST_ONLY: 'Post Only',
  IOC: 'Immediate Or Cancel',
};

function instructionLabelView(
  state: OrderFormState,
): OrderFormView['instructionLabel'] {
  return INSTRUCTION_VALUE_LABEL_MAP[state.instruction];
}

function limitPriceView({
  limitPrice,
  type,
}: OrderFormState): OrderFormView['limitPrice'] {
  if (type === 'MARKET') {
    return {
      value: '',
      formattedValue: '-',
      isValid: true,
    };
  }

  if (limitPrice == null) {
    return {
      value: '',
      formattedValue: '-',
      isValid: false,
    };
  }

  return {
    value: limitPrice.toString(),
    formattedValue: `$${limitPrice.toFormat()}`,
    isValid: limitPrice.isGreaterThan(0),
  };
}

function amountView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
): OrderFormView['amount'] {
  if (activeMarket == null || amount == null) {
    return {
      value: '',
      formattedValue: '-',
      isValid: false,
    };
  }

  return {
    value: amount.toString(),
    formattedValue:
      asset === activeMarket.quote_currency
        ? `$${amount.toFormat()}`
        : `${amount.toFormat()} ${asset}`,
    isValid: amount.isGreaterThan(0),
  };
}

function amountBaseAssetView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
  price: BigNumber | null,
): OrderFormView['amountBaseAsset'] {
  if (activeMarket == null || amount == null || price == null) {
    return {
      value: '',
      formattedValue: '-',
    };
  }

  const amountBaseAsset = convertToBaseAsset(
    asset,
    amount,
    price,
    activeMarket,
  );

  if (amountBaseAsset == null) {
    return {
      value: '',
      formattedValue: '-',
    };
  }

  return {
    value: amountBaseAsset.toString(),
    formattedValue: `${amountBaseAsset.toFormat(
      MarketFn.baseCurrencyDecimalPlaces(activeMarket),
    )} ${activeMarket.base_currency}`,
  };
}

function notionalQuoteAssetView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
  price: BigNumber | null,
): OrderFormView['notionalQuoteAsset'] {
  if (activeMarket == null || amount == null || price == null) {
    return {
      value: '',
      formattedValue: '-',
    };
  }

  const notionalQuoteAsset = convertToQuoteAsset(
    asset,
    amount,
    price,
    activeMarket,
  );
  return {
    value: notionalQuoteAsset.toString(),
    formattedValue: `$${notionalQuoteAsset.toFormat(SETTINGS.fiat.USD.scale)}`,
  };
}

function limitFeeView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
  price: BigNumber | null,
): OrderFormView['limitFee'] {
  if (activeMarket == null || amount == null || price == null) {
    return {
      value: '',
      formattedValue: '-',
    };
  }

  const amountQuoteAsset = convertToQuoteAsset(
    asset,
    amount,
    price,
    activeMarket,
  );
  const limitFee = calcLimitFee(amountQuoteAsset, 'TAKER');
  return {
    value: limitFee.toString(),
    formattedValue: `$${limitFee.toFormat(ORDER_FEE_PRECISION)}`,
  };
}

function feeRangeView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
  price: BigNumber | null,
): OrderFormView['feeRange'] {
  if (activeMarket == null || amount == null || price == null) {
    return '-';
  }

  const amountQuoteAsset = convertToQuoteAsset(
    asset,
    amount,
    price,
    activeMarket,
  );
  const takerFee = calcLimitFee(amountQuoteAsset, 'TAKER');
  const makerFee = calcLimitFee(amountQuoteAsset, 'MAKER');

  return `$${makerFee.toFormat()} - $${takerFee.toFormat()}`;
}

function convertToQuoteAsset(
  asset: string | null,
  amount: BigNumber,
  price: BigNumber,
  activeMarket: Market,
): BigNumber {
  if (asset == null) {
    return amount;
  }

  if (asset === activeMarket.base_currency) {
    return amount.multipliedBy(price);
  }

  if (asset === activeMarket.quote_currency) {
    return amount;
  }

  throw new Error(
    `${asset} does not match market ${activeMarket.base_currency}/${activeMarket.quote_currency} asset`,
  );
}

function convertToBaseAsset(
  asset: string | null,
  amount: BigNumber,
  price: BigNumber,
  activeMarket: Market,
): BigNumber | null {
  if (asset == null) {
    return amount;
  }

  if (asset === activeMarket.base_currency) {
    return amount;
  }

  if (asset === activeMarket.quote_currency) {
    const amountBaseAsset = amount.dividedBy(price);
    const value = amountBaseAsset.isFinite()
      ? amountBaseAsset.decimalPlaces(
          MarketFn.baseCurrencyDecimalPlaces(activeMarket),
        )
      : null;

    return value;
  }

  throw new Error(
    `${asset} does not match market ${activeMarket.base_currency}/${activeMarket.quote_currency} asset`,
  );
}

function calcLimitFee(amountQuoteAsset: BigNumber, type: FeeType) {
  return amountQuoteAsset
    .multipliedBy(getOrderFeePercent(type))
    .decimalPlaces(ORDER_FEE_PRECISION, BigNumber.ROUND_UP);
}
